import { z } from 'zod';
import {
    BusinessUnitSchema,
    IdDescriptionItemSchema,
    IWorklistAiDataSchema,
    IWorklistFeaDataSchema,
    ProvenanceExSchema,
    SeveritySchema,
    UserExSchema,
} from './iworklist.validation';
import { INreWorklistExamination } from '../models/nre';

// Schema for NRE-specific patient sex object
const NrePatientSexSchema = z
    .object({
        id: z.string(),
        description: z.string().nullable(),
        code: z.string().nullable(),
    })
    .strict();

// Schema for INreWorklistPatient
export const INreWorklistPatientSchema = z
    .object({
        id: z.string(),
        isLocal: z.boolean(),
        otherFields: z.string().nullable(),
        isLocked: z.boolean(),
        isAnonymous: z.boolean(),
        isExistInLocal: z.boolean(),
        searchType: z.number(),
        isPatientFromExternalSystem: z.boolean(),
        firstName: z.string().nullable(),
        lastName: z.string().nullable(),
        formularyNumber: z.string().nullable(),
        birthDate: z.string().nullable(),
        birthPlace: z.string().nullable(),
        sex: NrePatientSexSchema,
        taxCode: z.string().nullable(),
        identifier: z.string(),
        sourceId: z.string().nullable(),
        issuer: z.string().nullable(),
        sharedIssuer: z.string().nullable(),
        healthCard: z.string().nullable(),
        race: z.string().nullable(),
        civilStatus: z.string().nullable(),
        phone: z.string().nullable(),
        otherPhone: z.string().nullable(),
        email: z.string().nullable(),
        enterpriseRISIdentifier: z.string().nullable(),
        publicationUserName: z.string().nullable(),
        residenceAddress: z.string().nullable(),
        residenceZipCode: z.string().nullable(),
        residenceProvince: z.string().nullable(),
        residenceCity: z.string().nullable(),
        domicileAddress: z.string().nullable(),
        domicileZipCode: z.string().nullable(),
        domicileProvince: z.string().nullable(),
        domicileCity: z.string().nullable(),
        domicileAddressManagement: z.string().nullable(),
        deliveryAddress: z.string().nullable(),
        deliveryZipCode: z.string().nullable(),
        deliveryProvince: z.string().nullable(),
        deliveryCity: z.string().nullable(),
        deliveryPhone: z.string().nullable(),
        profession: z.string().nullable(),
        workPlaceAddress: z.string().nullable(),
        workPlaceZipCode: z.string().nullable(),
        workPlacePhone: z.string().nullable(),
        workPlaceCity: z.string().nullable(),
        team: z.string().nullable(),
        relative: z.string().nullable(),
        otherDataPhone: z.string().nullable(),
        notes: z.string().nullable(),
        stp: z.string().nullable(),
        eni: z.string().nullable(),
        doc: z.string().nullable(),
        taxCodeQuality: z.boolean(),
        taxCodeNplaQuality: z.boolean(),
        deathDate: z.string().nullable(),
        teamExpirationDate: z.string().nullable(),
        externalPatient: z.string().nullable(),
        hasFeaAuthorization: z.boolean(),
    })
    .strict();

// Schema for NRE exam details
const NreExamSchema = z
    .object({
        id: z.string(),
        description: z.string().nullable(),
        code: z.string().nullable(),
        otherCode: z.string().nullable(),
        doubleSigning: z.boolean(),
        modality: IdDescriptionItemSchema.extend({
            isMandatoryEquipmentChange: z.boolean(),
            patientCD: z.boolean(),
        }),
        isConsultation: z.boolean(),
        lesionDependent: z.boolean(),
        side: z.number(),
        templateFea: z.string().nullable(),
        idDoseClass: z.string().nullable(),
        hasDoseManagement: z.boolean(),
        isDefaultDoseUpdateable: z.boolean(),
        isMonitoring: z.boolean(),
        isRemoteVisit: z.boolean(),
        referringPhysicianMandatory: z.boolean(),
    })
    .strict();

// Schema for NRE equipment details
const NreEquipmentSchema = IdDescriptionItemSchema.extend({
    isWLSCU: z.boolean(),
    isManualSendToWorklist: z.boolean(),
    isEnabledSmsForQueue: z.boolean(),
    isImagesToBeAssociated: z.boolean(),
}).strict();

// Schema for NRE business unit details
const NreBusinessUnitSchema = BusinessUnitSchema.extend({
    code: z.number(),
    publicationWeb: z.boolean(),
    httpPacs: z.string().nullable(),
    isRemoteReportingSite: z.boolean(),
    isRadiology: z.boolean(),
    isToSendToCardio: z.boolean(),
    isBroker: z.boolean(),
    isRxDensityMandatory: z.boolean(),
    isSMII: z.boolean(),
    isSC: z.boolean(),
    patientCD: z.boolean(),
    isActive: z.boolean(),
    isSM: z.boolean(),
}).strict();

// Schema for NRE provenance details
const NreProvenanceSchema = ProvenanceExSchema.extend({
    externalCode: z.string(),
    provenanceGroupId: z.string(),
    isMandatoryBurden: z.boolean(),
    isMandatoryTicket: z.boolean(),
    publicationWeb: z.boolean(),
    isDensityMandatoryForSM: z.boolean(),
    patientCD: z.boolean(),
    isBookingEnable: z.boolean(),
    isActive: z.boolean(),
    groupProvenance: IdDescriptionItemSchema.nullable().optional(),
}).strict();

// Schema for authorization object
const AuthorizationSchema = z
    .object({
        id: z.string(),
        description: z.string().nullable(),
        isNRE: z.boolean(),
    })
    .strict();

// Schema for event object
const EventSchema = z
    .object({
        id: z.string(),
        admissionNumber: z.string().nullable(),
    })
    .strict();

// Schema for remote reporting data
const RemoteReportingDataSchema = z
    .object({
        sentTo: z.boolean(),
        receivedFrom: z.boolean(),
        sendingDate: z.string().nullable(),
        receivingDate: z.string().nullable(),
        remoteReportingBusinessUnit: z.string().nullable(),
    })
    .strict();

// Main schema for INreWorklistExamination
export const INreWorklistExaminationSchema = z
    .object({
        // Core identification
        id: z.string(),
        localId: z.string(),
        status: z.string().nullable(),
        accessionNumber: z.string().nullable(),

        // Dates
        lastDate: z.string().nullable(),
        date: z.string().nullable(),
        admissionDate: z.string().nullable(),
        plannedDate: z.string().nullable(),
        authorizationDate: z.string().nullable(),
        executionDate: z.string().nullable(),
        deliveryDate: z.string().nullable(),

        // Patient
        patient: INreWorklistPatientSchema,

        // Exam details
        exam: NreExamSchema,

        // Equipment and location
        equipment: NreEquipmentSchema,
        room: IdDescriptionItemSchema.nullable(),
        agenda: IdDescriptionItemSchema.nullable(),

        // Business unit and provenance
        businessUnit: NreBusinessUnitSchema,
        provenance: NreProvenanceSchema,

        // Personnel
        performingPhysician: UserExSchema.nullable(),
        requestingPhysician: UserExSchema.nullable(),
        referringPhysician: UserExSchema.nullable(),
        technician: UserExSchema.nullable(),
        nurse: UserExSchema.nullable(),
        anaesthetist: UserExSchema.nullable(),

        // Medical data
        severity: SeveritySchema.nullable(),
        diagnosis: z.string().nullable(),
        burden: z.string().nullable(),
        ticket: z.string().nullable(),
        feaData: IWorklistFeaDataSchema.nullable(),
        aiData: IWorklistAiDataSchema.nullable(),

        // Authorization and NRE specific
        authorization: AuthorizationSchema.nullable(),
        authorizationNumber: z.string().nullable(),
        isNRE: z.boolean(),

        // Clinical information
        reasonForStudy: z.string().nullable(),
        clinicalAlert: z.string().nullable(),
        notes: z.string().nullable(),

        // Event information
        event: EventSchema.nullable(),
        order: z.string().nullable(),

        // Status flags
        isPlanned: z.boolean(),
        isApproved: z.boolean(),
        isAdmitted: z.boolean(),
        isReported: z.boolean(),
        isExecuted: z.boolean(),
        isSuspended: z.boolean(),
        isSentToWorklist: z.boolean(),
        isManualSent: z.boolean(),
        isAvailabilityImages: z.boolean(),
        isAuthorizationResearchChecked: z.boolean(),
        isAuthorizationInternalChecked: z.boolean(),
        isAuthorizationExternalChecked: z.boolean(),
        isSM: z.boolean(),
        isSMII: z.boolean(),
        isSMSC: z.boolean(),
        isCompiledQuestionnaire: z.boolean(),
        isCompiledCaseHistory: z.boolean(),
        isCompiledCaseHistoryByXml: z.boolean(),
        isDeliveryDocumentationBack: z.boolean(),
        isQuestionnaireMandatory: z.boolean(),
        isMandatoryBurden: z.boolean(),
        isMandatoryTicket: z.boolean(),
        isEditingForExaminationDisabled: z.boolean(),

        // Additional identifiers
        admissionNumber: z.string().nullable(),
        externalNumber: z.string().nullable(),
        folderNumber: z.string().nullable(),
        hospitalizationNumber: z.string().nullable(),

        // Delivery information
        deliveryLocation: IdDescriptionItemSchema.nullable(),
        deliveryDocumentationType: z.string().nullable(),
        deliveryNote: z.string().nullable(),
        deliveryReceivingPerson: z.string().nullable(),

        // Patient state and measurements
        patientState: z.string().nullable(),
        height: z.number().nullable(),
        weight: z.number().nullable(),
        bsa: z.number().nullable(),

        // Medical imaging data
        mSec: z.number().nullable(),
        maSec: z.number().nullable(),
        kv: z.number().nullable(),
        ma: z.number().nullable(),
        xRayDosage: z.number().nullable(),
        exposureNumber: z.number().nullable(),
        studyInstanceUID: z.string().nullable(),

        // Notes and additional data
        admissionNotes: z.string().nullable(),
        executionNotes: z.string().nullable(),
        dynamicFields: z.string().nullable(),
        givingRadioactiveTracers: z.string().nullable(),

        // Delivery and publication
        isDeliverability: z.boolean(),
        reportLocalId: z.string().nullable(),
        pin: z.string().nullable(),
        email: z.string().nullable(),
        sms: z.string().nullable(),
        publicationAuthorization: z.boolean(),
        ticketPaid: z.boolean(),

        // Locking
        isLocked: z.boolean(),
        lockUser: z.string().nullable(),
        lockHost: z.string().nullable(),

        // Radio and dose management
        isRadio: z.boolean(),
        doses: z.string().nullable(),
        dosesDescription: z.string().nullable(),
        doseClass: z.string().nullable(),

        // External systems
        impetoProcessed: z.boolean(),
        impetoCode: z.number(),
        isImpeto: z.boolean(),
        isExternal: z.boolean().nullable(),

        // Remote reporting
        remoteReportingData: RemoteReportingDataSchema,

        // Additional management data
        myPebsManagementData: z.string().nullable(),
        contactPhoneNumber: z.string().nullable(),
        isEnabledSmsForQueue: z.boolean(),
        smsForQueue: z.string().nullable(),
        numberOfSmsSent: z.number(),
        lastSmsSendingDate: z.string().nullable(),
        deliverabilityUser: z.string().nullable(),

        // Permissions
        isAllowedToEditExamination: z.boolean(),
        isAllowedToAddExams: z.boolean(),
        isAllowedToEditExam: z.boolean(),

        // Order information
        placerGroupNumber: z.string().nullable(),
        placerOrderNumber: z.string().nullable(),

        // History and authorization tracking
        historyAuthorizationExternal: z.string().nullable(),
        historyAuthorizationInternal: z.string().nullable(),
        suspensionReason: z.string().nullable(),

        // Additional fields
        braceletNumber: z.string().nullable(),
        needsReferringPhysicianOnExecution: z.boolean(),
        obscuredDocument: z.boolean(),
        obscuredParent: z.boolean(),
        publicationUserName: z.string().nullable(),
        isDeleted: z.boolean(),
        isvNext: z.boolean(),
        revaluedCDA: z.boolean().nullable(),
        regionalConsent: z.string().nullable(),
        enterpriseRecordConsent: z.string().nullable(),
        historyConsent: z.string().nullable(),

        // Report information
        report: z.string().nullable(),
    })
    .strict();

export const validateNreDTO = (data: unknown): INreWorklistExamination => {
    return INreWorklistExaminationSchema.parse(data) as INreWorklistExamination;
};

export const applyNreValidation = (data: unknown, index: number) => {
    try {
        validateNreDTO(data);
    } catch (error: any) {
        console.error(`Validation failed for examination at index ${index}:`, error.errors);
        throw new Error(`Invalid examination data at index ${index}: ${error.message}`);
    }
};
